import { Text, Tooltip } from '@lego/klik-ui';
import {
  StatusErrorBold,
  StatusInprogress,
  StatusSuccessBold,
  StatusWarningBold,
} from '@lego/klik-ui/icons';
import { CopyableText } from '@/components/common/CopyableText';
import { PlaceholderText } from '@/components/common/PlaceholderText';
import { UserNameLink } from '@/components/common/UserNameLink';
import type { DeviceActionLogResponse } from '@/hooks/device/device_action_log_resource';
import { dateTimeFormat } from '@/utility/date';
import { createColumnHelper } from '../appTable';

type DeviceActionLogItem = DeviceActionLogResponse['items'][number];

const monoTruncateProps = {
  fontFamily: 'mono',
  fontSize: 'sm',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const truncateProps = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const columnHelper = createColumnHelper<DeviceActionLogItem>();

export const deviceActionLogTableColumns = columnHelper.columns([
  columnHelper.accessor('action', {
    cell: (info) => (
      <Text fontFamily="mono" fontSize="sm">
        {info.getValue()}
      </Text>
    ),
    enableSorting: true,
    header: 'Action',
    meta: { filterVariant: { type: 'singleSearch' } },
    size: 200,
  }),
  columnHelper.accessor('status', {
    cell: (info) => {
      const status = info.getValue();
      if (status === 'SUCCESS') {
        return (
          <Tooltip label="Success">
            <StatusSuccessBold color="green" />
          </Tooltip>
        );
      }
      if (status === 'ERROR') {
        return (
          <Tooltip label="Error">
            <StatusErrorBold color="red" />
          </Tooltip>
        );
      }
      if (status === 'ABORTED') {
        return (
          <Tooltip label="Aborted">
            <StatusWarningBold color="orange" />
          </Tooltip>
        );
      }
      return (
        <Tooltip label="Sent">
          <StatusInprogress color="grey" />
        </Tooltip>
      );
    },
    enableSorting: true,
    header: 'Status',
    meta: {
      filterVariant: {
        options: [
          { filterValue: 'SENT', name: 'Sent' },
          { filterValue: 'SUCCESS', name: 'Success' },
          { filterValue: 'ERROR', name: 'Error' },
          { filterValue: 'ABORTED', name: 'Aborted' },
        ],
        type: 'multiSelect',
      },
    },
    size: 96,
  }),
  columnHelper.accessor('errorMessage', {
    cell: (info) => {
      const value = info.getValue();
      if (!value) return null;
      return <CopyableText copyLabel="Copy error" textProps={truncateProps} value={value} />;
    },
    enableSorting: true,
    header: 'Error',
    meta: { filterVariant: { type: 'singleSearch' } },
    size: 240,
  }),
  columnHelper.accessor('payload', {
    cell: (info) => {
      const value = info.getValue();
      if (!value) return null;
      const formatted = Object.entries(value)
        .map(
          ([k, v]) => `${k}=${v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v)}`
        )
        .join(', ');
      return (
        <CopyableText copyLabel="Copy parameters" textProps={monoTruncateProps} value={formatted} />
      );
    },
    header: 'Parameters',
    size: 200,
  }),
  columnHelper.accessor('deviceTimestamp', {
    cell: (info) => {
      const value = info.getValue();
      return value ? (
        new Date(value).toLocaleString(dateTimeFormat.local)
      ) : (
        <PlaceholderText>N/A</PlaceholderText>
      );
    },
    enableSorting: true,
    header: 'Device Timestamp',
    meta: { filterVariant: { type: 'dateRange' } },
    size: 176,
  }),
  columnHelper.accessor('createdByOid', {
    cell: (info) => <UserNameLink oid={info.getValue()} />,
    enableSorting: false,
    header: 'Created By',
    size: 176,
  }),
  columnHelper.accessor('createdAt', {
    cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
    enableSorting: true,
    header: 'Created At',
    meta: { filterVariant: { type: 'dateRange' } },
    size: 176,
  }),
  columnHelper.accessor('updatedAt', {
    cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
    enableSorting: true,
    header: 'Updated At',
    meta: { filterVariant: { type: 'dateRange' } },
    size: 176,
  }),
  columnHelper.accessor('correlationId', {
    cell: (info) => {
      const value = info.getValue();
      if (!value) return null;
      return (
        <CopyableText copyLabel="Copy correlation ID" textProps={monoTruncateProps} value={value} />
      );
    },
    enableSorting: true,
    header: 'Correlation ID',
    meta: { filterVariant: { type: 'singleSearch' } },
    size: 300,
  }),
]);
